import {Anchor, createStyles} from '@mantine/core';
import React, {useCallback} from 'react';

import navigateToURL from 'lib/navigateToURL';
import {ChatSettingsTab} from 'llm/chat-library/sidepanel/AISidepanelV2GlobalClientState';
import {ACTIVITY_MODAL_KEY} from 'llm/chat-library/tool-details/ActivityModal';
import {chatSettingsModalUrl} from 'llm/routes/UrlUtils';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';
import * as LegacyModalContainer from 'ui-library/LegacyModalContainer';

// The memory tools wrap their output as `<memory id="agtmemsetvn_...">...</memory>` with one
// `<memory_file path="..." id="agtmemvn_...">` per file. ListMemoryFiles uses self-closing,
// path-only memory_file elements.
const MEMORY_SET_VERSION_ID_RE = /<memory\s+id="([^"]*)"/;
const MEMORY_FILE_RE = /<memory_file\s+path="([^"]*)"/g;

export interface ParsedMemoryOutput {
  // The whole-memory set version API id, or null when absent/unknown ("none").
  versionId: string | null;
  paths: Array<string>;
}

/**
 * Pull the set version id and file paths out of a memory tool's XML output. Returns null when the text
 * is not recognizably memory output.
 */
export function parseMemoryToolOutput(responseText: string | null | undefined): ParsedMemoryOutput | null {
  if (responseText == null || !responseText.includes('<memory')) {
    return null;
  }
  const versionIdMatch = MEMORY_SET_VERSION_ID_RE.exec(responseText);
  if (versionIdMatch == null) {
    return null;
  }
  const rawVersionId = versionIdMatch[1];
  const versionId = rawVersionId && rawVersionId !== 'none' ? rawVersionId : null;
  const paths: Array<string> = [];
  for (const fileMatch of responseText.matchAll(MEMORY_FILE_RE)) {
    const path = fileMatch[1];
    if (path && !paths.includes(path)) {
      paths.push(path);
    }
  }
  return {versionId, paths};
}

// A left-aligned action link (not a section header): regular weight with a leading history icon, so
// it reads as a click target distinct from the semibold "Input:"/"Output:" labels in ToolDetail.
const useStyles = createStyles(() => ({
  versionLink: {
    alignItems: 'center',
    alignSelf: 'flex-start',
    display: 'inline-flex',
    gap: 6,
  },
}));

interface MemoryVersionLinkProps {
  // The owning agent whose memory viewer the link opens.
  routineId: string;
  // The whole-memory set version API id from the tool output to open the viewer at.
  versionId: string;
}

/**
 * A single link (rendered at the top of the tool detail) that deep-links to this version of the
 * agent's memory in its viewer.
 */
export function MemoryVersionLink({routineId, versionId}: MemoryVersionLinkProps): JSX.Element {
  const {classes} = useStyles();

  const handleOpen = useCallback(() => {
    const url = chatSettingsModalUrl(ChatSettingsTab.AGENTS, {
      selectedItemId: routineId,
      memoryTarget: {versionId},
    });
    // Dismiss the tool-call activity modal so the settings modal is visible underneath.
    LegacyModalContainer.close(ACTIVITY_MODAL_KEY);
    navigateToURL(url);
  }, [routineId, versionId]);

  return (
    <Anchor className={classes.versionLink} component="button" onClick={handleOpen} size="sm" type="button">
      <BenchlingIcon aria-hidden={true} icon="history" />
      View this memory version
    </Anchor>
  );
}
